import {ChangeDetectionStrategy, Component, OnInit} from '@angular/core';
import {ActivatedRoute, Params, Router, UrlTree} from '@angular/router';
import {AuthService} from "../_services/auth.service";
import {NotificationService} from "../_services/notification.service";
import {SnackbarService} from "../_services/snackbar.service";
import {LocalStorageService} from "../_services/local-storage.service";
import {CommunitiesService} from "../_services/communities.service";
import {Community} from "../_types/communities";
import {AuthUser, SSOLoginContent} from "../_types/auth";

@Component({
    selector: 'app-passthrough',
    templateUrl: './passthrough.component.html',
    styleUrls: ['./passthrough.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PassthroughComponent implements OnInit {

    constructor(private route: ActivatedRoute,
                private readonly router: Router,
                private authService: AuthService,
                private notificationService: NotificationService,
                private readonly snack: SnackbarService,
                private readonly localStorageService: LocalStorageService,
                private readonly communitiesService: CommunitiesService) {
    }

    ngOnInit(): void {
        const verifiedLogin = this.authService.completedSSOEmailLogin;
        if (verifiedLogin) {
            this.authService.completedSSOEmailLogin = undefined;
            this.setSSOUserAndCommunity(verifiedLogin);
            return;
        }
        this.route.queryParams.subscribe((params): void => {
                if (params['app']) {
                    this.localStorageService.setItem(true, 'isApp')
                } else {
                    this.localStorageService.removeItem('isApp')
                }

                if (params['error']) {
                    console.error('No useful params why are you here?');
                    this.snack.openErrorSnackBar(params['error']);
                    this.authService.logout();
                } else if (params['magicToken']) {
                    this.authService.loginWithMagicToken(params['magicToken']).subscribe({
                        next: r => {
                            if (!r.verified) {
                                this.authService.logout();
                                return;
                            }
                            this.authService.setUserWithToken(r.auth_token, false, r.refresh).then((): void => {
                                this.setCommunityAndRedirect(r.auth_token, params)
                            }).catch((err: Error): void => {
                                console.error(err)
                            });

                        }, error: error => {
                            console.error(error)
                            // this.router.navigate(['/login']);
                        }
                    });
                } else if (params['token']) {
                    // Pass token to hijack the user
                    this.authService.setUserWithToken(params['token'], params['hijack']).then((): void => {
                        this.setCommunityAndRedirect(params['token'], params)
                    }).catch((err): void => {
                        this.authService.logout();
                    });
                } else if (params['notification']) {
                    this.notificationRouting(params)
                } else if (params['code'] || params['id_token']) {
                    // code (or Apple's id_token) present means this is an SSO callback
                    const provider: string | null = this.route.snapshot.paramMap.get('provider');
                    if (!provider) {
                        console.error('No provider why are you here?');
                        this.authService.logout();
                        return
                    }
                    this.loginWithProvider(provider, params)
                } else {
                    console.error('No useful params why are you here?');
                    this.authService.logout();
                }
            }
        );
    }

    loginWithProvider(provider: string, params: Params): void {
        const state: string = params['state'];
        const keepLoggedIn: boolean = params['keep_logged_in'];
        const referrer: string | undefined = this.route.snapshot.queryParamMap.get('referrer') ?? undefined;
        let body: SSOLoginContent;

        switch (provider) {
            case 'google':
            case 'microsoft':
            case 'slack':
            case 'facebook':
                // Authorization-code providers: the backend exchanges `code` using the
                // PKCE verifier / redirect_uri it stashed against `state`.
                body = {provider, code: params['code'], state, referrer};
                break;
            case 'apple':
                // Apple form-posts code/state/user to the API bridge, which 303s here
                // with them as query params. Same code exchange as the others, plus
                // `user` (name, first sign-in only) as a JSON string the backend parses.
                body = {provider, code: params['code'], user: params['user'], state, referrer};
                break;
            default:
                console.error('Not a provider we use');
                this.snack.openErrorSnackBar('An error occurred please try again.');
                this.authService.logout();
                return;
        }

        this.authService.SSOLogin(body, keepLoggedIn).subscribe({
            next: (res): void => {
                if ('email_verification' in res.data) {
                    this.router.navigate(['/login']);
                } else {
                    this.setSSOUserAndCommunity(res.data);
                }
            },
            error: (e): void => {
                this.snack.openErrorSnackBar(e);
                this.authService.logout();
            }
        });
    }

    setCommunityAndRedirect(token: string, params?: Params, user?: AuthUser): void {
        let returnUrl: string | null = params && params['returnUrl'] ? this.validateReturnUrl(decodeURIComponent(params['returnUrl'])) : null;

        let userObject = this.authService.getUser();
        if (userObject?.requires_sso_setup) {
            this.localStorageService.setItem({user: userObject, token, community_id: params?.['community_id']}, 'SSOUser');
            this.localStorageService.removeItem('currentUser');
            this.navigateToSSORegistration(userObject.email, params?.['community_id']);
            return;
        }
        this.communitiesService.getUserCommunities(token).subscribe(res => {
            if (res.length == 0) {
                console.error('How have you got no communities?');
                this.authService.logout();
            } else {
                if (user) {
                    this.authService.setUser(user);
                    this.localStorageService.removeItem('SSOUser');
                }
                let community: Community | null = res[0].community;
                if (params && params['community_id']) {
                    const i: number = res.findIndex((x: any): boolean => x.community.id == params['community_id']);
                    if (i != null && i >= 0) {
                        community = res[i].community
                        this.communitiesService.setCurrentDashboardCommunity(res[i].community)
                    } else {
                        this.communitiesService.setCurrentDashboardCommunity(res[0].community)
                    }
                } else {
                    this.communitiesService.setCurrentDashboardCommunity(res[0].community)
                }
                if (params && params['notification']) {
                    this.notificationRouting(params)
                } else if (returnUrl) {
                    this.router.navigateByUrl(returnUrl)
                } else if (community?.home_page_path) {
                    this.router.navigateByUrl(community.home_page_path);
                } else {
                    this.router.navigate(['/dashboard']);
                }
            }
        })
    }

    setSSOUserAndCommunity(data: AuthUser): void {
        if (data.mfa_required) {
            this.router.navigate(['/login']);
            return;
        }
        // Preserve the community restored by the API from the SSO state. The
        // shared callback host and the user's first membership may be different.
        const params: Params = {community_id: data.community_id};
        this.communitiesService.getSpecificCommunity(data.community_id).then((r): void => {
            let startingCommunity: Community = r as Community;
            this.communitiesService.getSpecificCommunity().then((r): void => {
                let endCommunity: Community = r as Community;

                if (startingCommunity.id && endCommunity.id && startingCommunity.id != endCommunity.id && startingCommunity.front_end_url) {
                    // if this is the alternative frontend url redirect to the frontend url with the passthrough and returnurl
                    let redirectURL: string = startingCommunity.front_end_url
                    if (!startingCommunity.front_end_url.endsWith("/")) {
                        redirectURL = redirectURL + '/'
                    }
                    const query = new URLSearchParams({
                        token: data.token,
                        community_id: String(startingCommunity.id)
                    });
                    redirectURL = redirectURL + 'passthrough?' + query.toString();
                    window.location.href = redirectURL;
                } else {
                    // otherwise just login here
                    this.completeSSOOnCurrentHost(data, params);
                }
            }).catch((err): void => {
                console.error(err);
                this.completeSSOOnCurrentHost(data, params);
            });

        }).catch((err): void => {
            console.error(err);
            this.completeSSOOnCurrentHost(data, params);
        });
    }

    private completeSSOOnCurrentHost(data: AuthUser, params: Params): void {
        this.localStorageService.setItem(data, 'SSOUser');
        if (data.user?.requires_sso_setup) {
            // Keep the token available for authenticated registration requests.
            this.authService.setUser(data);
            this.localStorageService.removeItem('currentUser');
            this.navigateToSSORegistration(data.user.email, data.community_id);
        } else {
            this.setCommunityAndRedirect(data.token, params, data);
        }
    }

    private navigateToSSORegistration(email: string, communityId?: number): void {
        const commands: any[] = ['/registrationStep', email];
        if (communityId) {
            commands.push({communityId});
        }
        this.router.navigate(commands);
    }

    notificationRouting(params: any): void {
        if (this.authService.isLoggedIn()) {
            // if the user is logged in then get notification and re-direct
            let returnUrl: string | null = this.validateReturnUrl(decodeURIComponent(params['returnUrl']))
            this.notificationService.getNotification(params['notification']).subscribe(r => {
                if (params['returnUrl'] && returnUrl) {
                    this.router.navigateByUrl(returnUrl).then((): void => {
                        this.notificationService.notificationRedirect(r)
                    });
                } else {
                    this.router.navigate(['/dashboard']).then((): void => {
                        this.notificationService.notificationRedirect(r)
                    });
                }
            }, e => {
                this.snack.openErrorSnackBar(e)
                this.router.navigate(['/dashboard'])
            })
        } else {
            // if the user is not logged in then get them to return here when they are
            this.authService.logout(window.location.pathname + window.location.search);
        }
    }

    validateReturnUrl(url: string): string | null {
        const validPathRegex = /^[a-zA-Z0-9\-._~\/]*$/;
        // Check if the URL matches the valid path pattern
        if (url != undefined && url && validPathRegex.test(url) && !url.includes('404')) {
            try {
                const urlTree: UrlTree = this.router.parseUrl(url);
                // If parseUrl doesn't throw an error, the URL is considered valid
                return url;
            } catch (e) {
                console.warn('Invalid URL:', url);
                // Handle invalid URL scenario
                return null;
            }
        } else {
            return null
        }
    }

}
